import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Dialog, DialogContent, List, ListItem,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import {
  compose, find, get, map, toNumber,
} from 'lodash/fp';

import { useModal } from '../../hooks';

const S = {
  Button: styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 40px;
    padding: 4px 8px 4px 12px;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    cursor: pointer;
    outline: none;
    :hover {
      border-color: ${({ theme, disabled }) => (disabled ? theme.palette.grey[300] : theme.palette.primary.main)};
    }
    :focus {
      padding-left: 11px;
      padding-right: 7px;
      border: 2px solid ${({ theme }) => theme.palette.primary.main};
    }
    :disabled {
      opacity: 0.38;
      color: ${({ theme }) => theme.palette.primary.main};
    }
  `,
  Value: styled.span`
    overflow: hidden;
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.grey[900]};
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  ExpandMore: styled(ExpandMore)`
    margin-left: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.25rem;
  `,
  Dialog: styled(Dialog)`
    .MuiPaper-root {
      margin: 40px;
    }
  `,
  DialogContent: styled(DialogContent)`
    padding: 0;
    :first-child {
      padding: 0;
    }
  `,
  ListItem: styled(ListItem)`
    display: block;
    overflow: hidden;
    padding: 16px 24px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1rem;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
};

const Filter = ({
  value, options, onSelect, disabled,
}) => {
  const [open, openDialog, closeDialog] = useModal();
  const selectedValue = useMemo(() => compose(
    get('label'),
    find((option) => option.value === value),
  )(options), [options, value]);

  const selectOption = useCallback((event) => {
    closeDialog();

    onSelect(toNumber(event.currentTarget.dataset.value));
  }, [closeDialog, onSelect]);

  return (
    <>
      <S.Button type="button" onClick={openDialog} disabled={disabled}>
        <S.Value>
          {selectedValue}
        </S.Value>
        <S.ExpandMore />
      </S.Button>
      <S.Dialog open={open} onClose={closeDialog}>
        <S.DialogContent>
          <List>
            {map((option) => (
              <S.ListItem key={option.id} data-value={option.value} onClick={selectOption}>
                {option.label}
              </S.ListItem>
            ), options)}
          </List>
        </S.DialogContent>
      </S.Dialog>
    </>
  );
};

Filter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
  })),
  value: PropTypes.number,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
};

Filter.defaultProps = {
  options: null,
  value: 0,
  onSelect: () => {},
  disabled: false,
};

export default Filter;
